import React from 'react';
import moment from 'moment';
import { Grid } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import { SlackPHDaysInAdvanceOptions } from '../../../api/settings';
import { getFriendlyTime } from '../../../../utils/datetime';
import { toCapitalCase } from '../../../../utils/strings';
import { PauseModal, PauseModalBody, PauseModalFooter, PauseModalTitle } from '../../../components/PauseModal';
import { SLACK_PH_FREQUENCIES } from '../../../../utils/constants';
import { PauseForm } from '../../../components/PauseForm';
import { PauseFormAutocomplete } from '../../../components/PauseFormField2/PauseFormAutocomplete';
import { PauseButton } from '../../../components/PauseButton';
import { PauseSubmitButton } from '../../../components/PauseSubmitButton';
import { PauseTypography } from '../../../components/PauseTypography';
import { noop } from '../../../api/_utils';
import { PauseFormSelect } from '../../../components/PauseFormField2/PauseFormSelect';

const InfoText = ({
  frequencies = SLACK_PH_FREQUENCIES,
}: {
  frequencies: { label: string; value: SlackPHDaysInAdvanceOptions }[];
}) => {
  const formik = useFormikContext();
  const getSelectedFrequency = (freq: SlackPHDaysInAdvanceOptions) => frequencies.find(({ value }) => value === freq);
  return (
    <PauseTypography
      css={(theme) => ({
        marginTop: theme.spacing(2),
      })}
      variant="body2"
    >
      Notifications will be sent every week, from{' '}
      {getSelectedFrequency(get(formik, 'values.daysInAdvance.value', SlackPHDaysInAdvanceOptions.FourWeeks))?.label}{' '}
      prior to a public holiday.
    </PauseTypography>
  );
};
interface Props extends React.PropsWithChildren<{}> {
  channel: string;
  slackChannels: Array<{
    label: string;
    value: string;
  }>;
  frequencies?: Array<{
    label: string;
    value: SlackPHDaysInAdvanceOptions;
  }>;
  daysInAdvance: SlackPHDaysInAdvanceOptions;
  time: string;
  dayOfWeek: string;
  onClose: () => any;
  onToggleSetting: (options: {
    type: 'publicHolidays';
    enabled: true;
    options: {
      channel: string;
      daysInAdvance: SlackPHDaysInAdvanceOptions;
      time: string;
      dayOfWeek: string;
    };
  }) => any;
}

const hours = Array.from(
  {
    length: 48,
  },
  (_, hour) => {
    const time = moment({
      hour: Math.floor(hour / 2),
      minutes: hour % 2 === 0 ? 0 : 30,
    });
    return {
      value: time.format('HH:mm:ss'),
      label: time.format('h:mm a'),
    };
  },
);

const daysOfWeek = moment.weekdays().map((d) => ({
  label: d,
  value: d.toUpperCase(),
}));

export const PublicHolidaySlackNotificationSettingsModal = ({
  channel = '',
  slackChannels = [],
  frequencies = SLACK_PH_FREQUENCIES,
  daysInAdvance = SlackPHDaysInAdvanceOptions.FourWeeks,
  time = '09:00:00',
  dayOfWeek = 'MONDAY',
  onClose = () => null,
  onToggleSetting = () => null,
  ...props
}: Props) => {
  const getSelectedChannel = (channelID) => slackChannels.find(({ value }) => value === channelID);
  // const getSelectedFrequency = (freq: SlackPHDaysInAdvanceOptions) => frequencies.find(({ value }) => value === freq);
  const friendlyTime = getFriendlyTime(time);
  const dayofWeekDisplay = toCapitalCase(dayOfWeek);
  const initialValues = {
    channel: getSelectedChannel(channel),
    // daysInAdvance: getSelectedFrequency(daysInAdvance),
    daysInAdvance,
    time,
    dayOfWeek,
  };
  return (
    /* @ts-ignore */
    <PauseForm
      enableReinitialize
      initialValues={initialValues}
      onSubmit={async (values) => {
        const data: {
          channel: string;
          daysInAdvance: SlackPHDaysInAdvanceOptions;
          time: string;
          dayOfWeek: string;
        } = {
          channel: values.channel.value,
          daysInAdvance: values.daysInAdvance,
          time: values.time,
          dayOfWeek: values.dayOfWeek,
        };
        try {
          await onToggleSetting({
            type: 'publicHolidays',
            enabled: true,
            options: data,
          });
          onClose();
        } catch (e) {
          noop();
        }
      }}
    >
      {/* @ts-ignore */}
      <PauseModal open width="20vw" onClose={onClose} {...props}>
        <PauseModalTitle
          title="Public Holiday Notifications"
          // description={`These notifications will be sent on ${dayofWeekDisplay} at ${friendlyTime}`}
          onClose={onClose}
        />
        <PauseModalBody>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <PauseFormAutocomplete
                fieldProps={{
                  options: slackChannels,
                  label: 'Channel',
                }}
                name="channel"
              />
            </Grid>
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12}>
                <PauseTypography variant="caption">When do you want notifications?</PauseTypography>
              </Grid>
              <Grid item xs={12}>
                <PauseFormSelect
                  fieldProps={{
                    options: frequencies,
                    label: 'Frequency',
                  }}
                  name="daysInAdvance"
                />
                <InfoText frequencies={frequencies} />
              </Grid>
              <Grid item xs={12}>
                <PauseFormSelect
                  fieldProps={{
                    options: daysOfWeek,
                    label: 'Day of the notifications',
                  }}
                  name="dayOfWeek"
                />
              </Grid>
              <Grid item xs={12}>
                <PauseFormSelect
                  fieldProps={{
                    options: hours,
                    label: 'Time of the notifications',
                  }}
                  name="time"
                />
              </Grid>
            </Grid>
          </Grid>
          <div />
        </PauseModalBody>
        <PauseModalFooter>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <PauseButton color="error" variant="outlined" onClick={onClose}>
                Cancel
              </PauseButton>
            </Grid>
            <Grid item xs={6}>
              <PauseSubmitButton>
                <PauseButton>Save</PauseButton>
              </PauseSubmitButton>
            </Grid>
          </Grid>
        </PauseModalFooter>
      </PauseModal>
    </PauseForm>
  );
};
